import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ChevronLeft } from "../../assets/icons/ChevronLeft";
import { ChevronRight } from "../../assets/icons/ChevronRight";
import {
  Ellipsis,
  PageButton,
  PaginationOuter,
  PaginationPill,
} from "./Pagination.styles";

export interface IPaginationProps {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
}

type PageItem = number | "ellipsis-left" | "ellipsis-right";

function getPageItems(currentPage: number, totalPages: number): PageItem[] {
  if (totalPages <= 1) return [1];

  // Show first, last, and a window of ±2 around the current page so on
  // small spans (1-7 pages) the user sees every page button without `…`.
  const pagesToShow = new Set<number>([1, totalPages, currentPage]);
  for (let offset = 1; offset <= 2; offset += 1) {
    if (currentPage - offset >= 1) pagesToShow.add(currentPage - offset);
    if (currentPage + offset <= totalPages)
      pagesToShow.add(currentPage + offset);
  }

  const sortedPages = Array.from(pagesToShow).sort((a, b) => a - b);

  const items: PageItem[] = [];
  for (let i = 0; i < sortedPages.length; i += 1) {
    const page = sortedPages[i];
    if (i > 0 && page - sortedPages[i - 1] > 1) {
      items.push(
        sortedPages[i - 1] < currentPage ? "ellipsis-left" : "ellipsis-right"
      );
    }
    items.push(page);
  }

  return items;
}

export function Pagination({
  currentPage,
  pageSize,
  totalItems,
  onPageChange,
}: IPaginationProps) {
  const { t } = useTranslation();

  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const isFirstPage = currentPage <= 1;
  const isLastPage = currentPage >= totalPages;

  const pageItems = useMemo(
    () => getPageItems(currentPage, totalPages),
    [currentPage, totalPages]
  );

  // Hide entirely when there's only a single page of results.
  if (totalPages <= 1) return null;

  const handlePrev = () => {
    if (!isFirstPage) onPageChange(currentPage - 1);
  };
  const handleNext = () => {
    if (!isLastPage) onPageChange(currentPage + 1);
  };

  return (
    <PaginationOuter>
      <PaginationPill>
        <PageButton
          type="button"
          aria-label={t("pagination_previousPage")}
          onClick={handlePrev}
          disabled={isFirstPage}
          isDisabled={isFirstPage}
        >
          <ChevronLeft opacity={1} />
        </PageButton>

        {pageItems.map((item) => {
          if (item === "ellipsis-left" || item === "ellipsis-right") {
            return (
              <Ellipsis
                key={item}
                role="presentation"
                aria-label={t("pagination_morePages")}
              >
                …
              </Ellipsis>
            );
          }

          const isActive = item === currentPage;
          return (
            <PageButton
              key={item}
              type="button"
              aria-label={t("pagination_page", { page: item })}
              aria-current={isActive ? "page" : undefined}
              active={isActive}
              onClick={() => onPageChange(item)}
            >
              {item}
            </PageButton>
          );
        })}

        <PageButton
          type="button"
          aria-label={t("pagination_nextPage")}
          onClick={handleNext}
          disabled={isLastPage}
          isDisabled={isLastPage}
        >
          <ChevronRight opacity={1} />
        </PageButton>
      </PaginationPill>
    </PaginationOuter>
  );
}
